import { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
  Collapse,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import useIsMobile from "components/hooks/useIsMobile";
import {
  Activity,
  ChevronDown,
  ChevronUp,
  CircleAlert,
  CreditCard,
  RefreshCcw,
  Sparkles,
  Star,
  Tag,
  Timer,
  UserRoundCheck,
} from "lucide-react";
import LinkRouter from "components/navigation/LinkRouter";
import { useUpdateInvoice } from "services/billing";
import { Invoice, UpdateInvoiceRequest } from "protogen/billing_service_pb";
import { feeSchedule } from "components/family/utils";
import InvoiceStatusSelect from "components/payments/InvoiceStatusSelect";
import { PartialMessage } from "@bufbuild/protobuf";
import AdvisorSelect from "../../components/creation/AdvisorSelect";
import { formatDateRange, formatPrice, shortDate } from "common/utils";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import InvoiceHistoryFeed from "./InvoiceHistoryFeed";
import FlexBox from "components/common/FlexBox";
import { ReactComponent as FamilyIcon } from "../../icons/Family.svg";

const InvoiceFlags = ({ invoice }: { invoice: Invoice }) => {
  const isExtraHours = invoice.transactionType === "extra-hours";

  const hasFlags =
    isExtraHours ||
    // This will almost always be true, we might just want to drop this if.
    invoice.planUtilization?.planHoursAllocated ||
    !invoice.isAdvisorPayable ||
    invoice.flags?.isAdvisorNew ||
    invoice.flags?.isRecentRematch ||
    invoice.flags?.advisorIsWrong ||
    invoice.serviceMonth <= 1 ||
    invoice.subtotalCents > invoice.invoiceAmountCents ||
    invoice.flags?.isBillingChange;

  if (!hasFlags) return null;
  const utilization = invoice.planUtilization?.planHoursAllocated
    ? (invoice.planUtilization?.periodHoursUtilized || 0) /
      (invoice.planUtilization?.planHoursAllocated || 1)
    : null;

  return (
    <Box sx={{ display: "flex", gap: "8px", padding: "8px 0 0" }}>
      {isExtraHours && (
        <Chip
          variant="green"
          icon={<Timer size={16} />}
          label="Extra hours"
          color="warning"
          size="small"
        />
      )}
      {invoice.flags?.isAdvisorNew && (
        <Chip
          variant="green"
          icon={<Sparkles size={16} />}
          label="New Advisor"
          color="warning"
          size="small"
        />
      )}
      {!isExtraHours && utilization !== null && (
        <Chip
          variant={
            utilization > 0.5 ? "green" : utilization === 0 ? "red" : "orange"
          }
          icon={<Activity size={16} />}
          label={`${Math.round(utilization * 100)}% utilization`}
          color="warning"
          size="small"
        />
      )}

      {invoice.flags?.isRecentRematch && (
        <Chip
          variant="green"
          icon={<RefreshCcw size={16} />}
          label="Recent rematch"
          color="warning"
          size="small"
        />
      )}
      {!isExtraHours && invoice.serviceMonth <= 1 && (
        <Chip
          variant="green"
          icon={<Star size={16} />}
          label="First invoice"
          size="small"
        />
      )}
      {invoice.subtotalCents > invoice.invoiceAmountCents && (
        <Chip
          variant="green"
          icon={<Tag size={16} />}
          label="Discount applied"
          color="warning"
          size="small"
        />
      )}
      {invoice.flags?.isBillingChange && (
        <Chip
          variant="green"
          icon={<CreditCard size={16} />}
          label="Billing change"
          color="warning"
          size="small"
        />
      )}
      {invoice.flags?.advisorIsWrong && (
        <Chip
          variant="red"
          icon={<CircleAlert size={16} />}
          label="Advisor is wrong"
          color="warning"
          size="small"
        />
      )}
      {!invoice.isAdvisorPayable && (
        <Chip
          variant="red"
          icon={<CircleAlert size={16} />}
          label={
            invoice.advisor?.ref
              ? "Advisor is not linked to a bank account for payments."
              : "Invoice not associated with an advisor."
          }
          color="warning"
          size="small"
        />
      )}
    </Box>
  );
};

// -- Edit Invoice Modal
const EditInvoice = ({
  invoice,
  onClose,
  update,
  loading,
}: {
  invoice: Invoice;
  onClose: () => void;
  update: (data: PartialMessage<UpdateInvoiceRequest>) => void;
  loading: boolean;
}) => {
  const [paymentDollars, setPaymentDollars] = useState(
    invoice.paymentCents / 100 ?? 0,
  );
  const [changeNotes, setChangeNotes] = useState("");
  const [advisorRef, setAdvisorRef] = useState<string | null>(
    invoice.advisor?.ref,
  );

  const onSave = () => {
    // Convert back to original units when saving
    update({
      setPaymentCentsZero:
        Math.round(paymentDollars * 100) !== invoice.paymentCents &&
        paymentDollars === 0,
      paymentCents:
        Math.round(paymentDollars * 100) !== invoice.paymentCents
          ? Math.round(paymentDollars * 100)
          : undefined,
      changeNotes,
      advisorRef:
        advisorRef && advisorRef !== invoice.advisor?.ref
          ? advisorRef
          : undefined,
    });
  };

  return (
    <Box display="flex" flexDirection="column" gap="16px" padding="16px">
      <AdvisorSelect
        isAdmin={true}
        selectedAdvisor={advisorRef}
        title={"Payment recipient"}
        onChange={(ref) => setAdvisorRef(ref)}
      />
      <TextField
        label="Payment"
        type="number"
        value={paymentDollars}
        onChange={(e) => setPaymentDollars(parseFloat(e.target.value))}
        inputProps={{ min: 0, step: 0.01 }}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{
                margin: "0 0 0 20px",
                paddingTop: "16px",
              }}
            >
              $
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiInputBase-input": {
            padding: "0 0 0 6px",
          },
        }}
      />
      <TextField
        label="Change Notes"
        multiline
        rows={4}
        value={changeNotes}
        onChange={(e) => setChangeNotes(e.target.value)}
        fullWidth
      />
      <Box justifyContent="end" display="flex" flexDirection="row" gap="8px">
        <Button onClick={onClose} variant="text" disabled={loading}>
          Close
        </Button>
        <Button onClick={onSave} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

// Add debounce utility
const debounce = (fn: Function, ms = 300) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn.apply(this, args), ms);
  };
};

// -- Single Invoice Card
export default ({
  invoice,
  refresh,
  setAdvisorRef,
  setFamilyRef,
  onHeightChange,
}: {
  invoice: Invoice;
  refresh: () => Promise<void>;
  setAdvisorRef: (r: string) => void;
  setFamilyRef: (r: string) => void;
  onHeightChange?: (height: number) => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [viewMode, setViewMode] = useState<"edit" | "history">("history");
  const isMobile = useIsMobile();
  const [updateLock, setUpdateLock] = useState(false);
  const { pushSnack } = useSnackpack();
  const { request, loading } = useUpdateInvoice();
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cardRef.current && onHeightChange) {
      // Create a debounced version of the height update
      const debouncedHeightUpdate = debounce((height: number) => {
        onHeightChange(height);
      }, 10); // 100ms debounce time

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const style = window.getComputedStyle(entry.target);
          const marginTop = parseFloat(style.marginTop);
          const marginBottom = parseFloat(style.marginBottom);
          debouncedHeightUpdate(
            entry.contentRect.height + marginTop + marginBottom,
          );
        }
      });
      resizeObserver.observe(cardRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [onHeightChange]);

  const update = async (data: PartialMessage<UpdateInvoiceRequest>) => {
    if (loading || updateLock) return;
    setUpdateLock(true);
    await request(
      new UpdateInvoiceRequest({
        invoiceRef: invoice.ref,
        ...data,
        skipPaymentApproval: true,
      }),
    );
    pushSnack("Invoice updated");
    await refresh();
    setUpdateLock(false);
  };

  const byline = (
    <Box display={"flex"} flexDirection={"column"}>
      {/* Family name & links */}
      <Box display="flex" flexDirection="row" alignItems="center" gap="4px">
        <Typography
          variant="bodyHeavy"
          sx={{ cursor: "pointer" }}
          color="text.primary"
          onClick={(e) => {
            e.stopPropagation();
            if (invoice.family?.ref) {
              setFamilyRef(invoice.family.ref);
            }
          }}
        >
          {invoice.family?.name}
        </Typography>
        <Typography variant="body">paid</Typography>
        <LinkRouter
          targetNew={true}
          to={`https://dashboard.stripe.com/invoices/${invoice.stripeId}`}
          sx={{
            alignItems: "center",
          }}
        >
          <Tooltip
            title={`${formatPrice(
              invoice.subtotalCents,
              invoice.invoiceCurrency,
            )}`}
          >
            <Typography variant="body">
              {formatPrice(invoice.invoiceAmountCents, invoice.invoiceCurrency)}
              {invoice.subtotalCents !== invoice.invoiceAmountCents ? "*" : ""}
            </Typography>
          </Tooltip>
        </LinkRouter>
      </Box>

      {invoice.periodStartSec && (
        <Typography variant="bodySmall" color="text.secondary">
          {`${formatDateRange(invoice.periodStartSec, invoice.periodEndSec)}`}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box
      ref={cardRef}
      display="flex"
      flexDirection="column"
      sx={{
        borderRadius: "12px",
        border: "1px solid #EAEBEC",
        width: "100%",
        marginBottom: "16px",
      }}
    >
      <Box
        sx={{
          cursor: !expanded ? "pointer" : undefined,
          padding: "20px 24px",
        }}
      >
        <Box sx={{ display: "flex", gap: "8px" }}>
          {/* External links */}
          <FlexBox
            direction="column"
            justifyContent="start"
            alignItems="center"
            gap="6px"
            sx={{ height: "100%" }}
          >
            <LinkRouter
              to={`/tools/advisor-administration/${encodeURIComponent(
                invoice.advisor?.ref ?? "",
              )}`}
              sx={{ padding: "0" }}
            >
              <UserRoundCheck size={20} color="#8E9598" />
            </LinkRouter>
            <LinkRouter
              to={`/families/${invoice.family?.ref}?adminView=1#tab-admin`}
              sx={{ padding: "0" }}
            >
              <FamilyIcon width="22px" height="22px" fill="#8E9598" />
            </LinkRouter>
          </FlexBox>

          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isMobile ? "start" : "center"}
            gap="12px"
            width="100%"
            onClick={() => !expanded && setExpanded(true)}
          >
            {/* Invoice info */}
            <Box flex="5">
              <Box display="flex" flexDirection={"column"}>
                <Box
                  display="flex"
                  flexDirection={isMobile ? "column" : "row"}
                  gap="4px"
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap="4px"
                  >
                    <Typography
                      variant="h4"
                      color="#262626"
                      sx={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (invoice.advisor?.ref) {
                          setAdvisorRef(invoice.advisor.ref);
                        }
                      }}
                    >
                      {`${invoice.advisor?.firstName} ${invoice.advisor?.lastName}`
                        .length <= 12
                        ? `${invoice.advisor?.firstName} ${invoice.advisor?.lastName}`
                        : `${invoice.advisor?.firstName} ${invoice.advisor?.lastName[0]}.`}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  variant="body"
                  color="text.secondary"
                  sx={{ textWrap: "none" }}
                >
                  {byline}
                </Typography>
              </Box>
            </Box>

            {/* Service months */}
            <Box flex="1" display="flex" gap="4px" alignItems="center">
              {isMobile && (
                <Typography variant="bodyHeavy">Service months:</Typography>
              )}
              <Tooltip
                title={
                  invoice.family?.startDate &&
                  `Started ${shortDate(invoice.family?.startDate)}`
                }
              >
                <Typography variant="body" color="text">
                  {invoice.serviceMonth}
                </Typography>
              </Tooltip>
            </Box>

            {/* Percentage */}
            <Box
              flex="1"
              display="flex"
              gap="4px"
              alignItems={isMobile ? "start" : "center"}
            >
              {isMobile && <Typography variant="bodyHeavy">Fee:</Typography>}
              <Tooltip title={feeSchedule(invoice.family?.platformFeeSchedule)}>
                <Typography variant="body" color="text">
                  {invoice.feePercent * 100}%
                </Typography>
              </Tooltip>
            </Box>

            {/* Amount */}
            <Box
              flex="2"
              display="flex"
              flexDirection="column"
              alignItems="center"
            >
              <Tooltip
                title={`${formatPrice(
                  invoice.subtotalCents,
                  invoice.invoiceCurrency,
                )} - ${invoice.feePercent * 100}%`}
              >
                <Typography
                  variant="bodyHeavy"
                  color="text.primary"
                  sx={{
                    textAlign: "right",
                  }}
                >
                  {formatPrice(invoice.paymentCents, "USD")}
                </Typography>
              </Tooltip>
              {invoice.invoiceAmountCents !== invoice.subtotalCents && (
                <Typography variant="bodySmall" color="text.tertiary">
                  {formatPrice(
                    invoice.subtotalCents - invoice.invoiceAmountCents,
                    invoice.invoiceCurrency,
                  )}{" "}
                  off
                </Typography>
              )}
            </Box>

            {/* Actions */}
            <Box flex="1" display="flex" alignItems="center" gap="8px">
              <Box onClick={(e) => e.stopPropagation()}>
                <InvoiceStatusSelect
                  loading={loading || updateLock}
                  paymentDisabled={
                    invoice.status === "pending_approval" &&
                    !invoice.isAdvisorPayable
                  }
                  status={invoice.status}
                  setStatus={(s) => update({ status: s })}
                  onEdit={() => {
                    setExpanded(true);
                    setViewMode("edit");
                  }}
                />
              </Box>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => {
                  setExpanded(!expanded);
                  setViewMode("history");
                }}
              >
                {expanded ? <ChevronUp /> : <ChevronDown />}
              </IconButton>
            </Box>
          </Box>
        </Box>
        <FlexBox justifyContent="space-between" alignItems="center">
          <InvoiceFlags invoice={invoice} />
        </FlexBox>
      </Box>

      {/* Collapsed content */}
      <Collapse in={expanded} timeout={1000}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            background: "white",
            padding: "0px 24px 16px",
          }}
        >
          {/* Change to an edit mode */}
          {viewMode === "edit" && (
            <EditInvoice
              invoice={invoice}
              onClose={() => setExpanded(false)}
              update={update}
              loading={loading}
            />
          )}
          {expanded && viewMode === "history" && invoice.family?.ref && (
            <InvoiceHistoryFeed invoice={invoice} />
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
